import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { AuthService } from '../../services/AuthService/auth';
import { ExportRequestService } from '../../services/ExportRequestService/export-request';
import { AdminService } from '../../services/AdminService/admin';
import { ProjectService } from '../../services/ProjectService/project';
import { WalletService } from '../../services/WalletService/wallet';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { AuditEventType } from '../../models/audit-event.dto';
import { AdminExportRequestResponse, ExportRequestStatus } from '../../models/export-request.dto';
import { CompanyStatus } from '../../models/company.dto';
import { ApiEnvelope, Role } from '../../models/auth.dto';
import * as perms from '../../shared/permissions';

export type ChartPeriod = 'week' | 'month' | 'year';

interface ChartBucket {
  label: string;
  start: Date;
  end: Date;
  count: number;
  accepted: number;
}

interface BarLayout {
  label: string;
  count: number;
  accepted: number;
  acceptedRate: number;
  heightPct: number;
  /** Distance from the baseline to the accepted point, as a % of plot height. */
  acceptedPct: number;
}

interface AxisTick {
  value: number;
  pct: number;
}

interface ChartRender {
  bars: BarLayout[];
  acceptedPath: string;
  ticks: AxisTick[];
}

/** Share of the plot height the tallest bar occupies, leaving headroom for the trend line. */
const PLOT_SCALE = 85;
const MAX_TREND_PAGES = 5;

// Donut geometry. The ring is drawn as one <circle> per slice with a dash pattern, so a
// slice is a stroke segment rather than a filled path — that keeps every arc the same
// width and lets the gap between slices be expressed directly in arc length.
const DONUT_RADIUS = 54;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
/** Surface gap between adjacent slices, in arc length units (~2px of stroke). */
const DONUT_GAP = 3;

export interface DonutArc {
  key: string;
  label: string;
  color: string;
  count: number;
  pct: number;
  /** `stroke-dasharray` for the slice's visible run. */
  dash: string;
  /** `stroke-dashoffset` placing the run at its position around the ring. */
  offset: number;
}

// Fixed clockwise-from-top order for both status rings. These are the app's semantic
// status hues (shared with `shared/status-colors.ts`), so they're a *status* palette, not
// a free categorical one — several pairs sit below the categorical CVD gate by nature of
// being red/amber/green. The order is chosen so the two worst pairs are never adjacent
// arcs (amber never touches red; red only wraps onto green), and every slice is
// direct-labeled with its count and share in the legend plus separated by a 2px surface
// gap, so hue never carries the meaning alone.
const STATUS_CHART_ORDER: { status: ExportRequestStatus; label: string; color: string }[] = [
  { status: 'ACCEPTED', label: 'Accepted', color: '#15803d' },
  { status: 'PENDING', label: 'Pending', color: '#d97706' },
  { status: 'PREPARING', label: 'Preparing', color: '#1d4ed8' },
  { status: 'DECLINED', label: 'Declined', color: '#e11d48' },
  { status: 'TRACE_FAILED', label: 'Trace failed', color: '#64748b' },
];

const COMPANY_STATUS_ORDER: { status: CompanyStatus; label: string; color: string }[] = [
  { status: 'APPROVED', label: 'Approved', color: '#15803d' },
  { status: 'PENDING', label: 'Pending', color: '#d97706' },
  { status: 'SUSPENDED', label: 'Suspended', color: '#64748b' },
  { status: 'REJECTED', label: 'Rejected', color: '#e11d48' },
];

const ROLE_ORDER: { role: Role; label: string }[] = [
  { role: 'SUPERADMIN', label: 'Super admin' },
  { role: 'ADMIN', label: 'Admin' },
  { role: 'CUSTOMS', label: 'Customs' },
  { role: 'AUDITOR', label: 'Auditor' },
  { role: 'COMPANY_ADMIN', label: 'Company admin' },
  { role: 'COMPANY_USER', label: 'Company user' },
];

const PLATFORM_SCOPED: Role[] = ['SUPERADMIN', 'ADMIN', 'CUSTOMS', 'AUDITOR'];

export type CompanyFilter = CompanyStatus | 'ALL';

export const COMPANY_FILTERS: { value: CompanyFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

export type StatAccent = 'blue' | 'amber' | 'green' | 'violet';

export interface StatTile {
  icon: string;
  label: string;
  value: string;
  link: string;
  accent: StatAccent;
}

export interface ActivityItem {
  icon: string;
  title: string;
  subtitle: string;
  time: string;
  link: string;
  badgeStatus: string;
}

export interface CompanyListItem {
  id: string;
  name: string;
  countryCode: string;
  registrationNo: string;
  status: CompanyStatus;
  createdAt: string;
}

export interface RoleSummaryRow {
  role: Role;
  label: string;
  count: number;
  /** Bar length as a % of the largest role, so the longest row always fills the track. */
  widthPct: number;
}

export interface RecentUserItem {
  id: string;
  fullName: string;
  email: string;
  role: string;
  enabled: boolean;
}

@Component({
  selector: 'app-home',
  imports: [CommonModule, RouterLink, StatusBadge],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit {
  session: ReturnType<AuthService['getSession']>;
  stats = signal<StatTile[]>([]);
  recentActivity = signal<ActivityItem[]>([]);
  recentActivityTitle = signal('');
  recentActivityLink = '';
  isLoadingActivity = signal(false);
  statusArcs = signal<DonutArc[]>([]);
  statusBreakdownTotal = signal(0);
  isLoadingBreakdown = signal(false);

  readonly companyFilters = COMPANY_FILTERS;
  companyFilter = signal<CompanyFilter>('ALL');
  companies = signal<CompanyListItem[]>([]);
  companyArcs = signal<DonutArc[]>([]);
  companyTotal = signal(0);
  isLoadingCompanies = signal(false);
  isLoadingCompanyCounts = signal(false);

  roleSummary = signal<RoleSummaryRow[]>([]);
  platformScopedTotal = signal(0);
  companyScopedTotal = signal(0);
  recentUsers = signal<RecentUserItem[]>([]);
  isLoadingUsers = signal(false);
  isLoadingRoles = signal(false);

  chartPeriod = signal<ChartPeriod>('month');
  chartTotal = signal(0);
  chartAccepted = signal(0);
  chartPeakLabel = signal('—');
  chartRender = signal<ChartRender | null>(null);
  isLoadingChart = signal(false);
  today = new Date();

  readonly donutCircumference = DONUT_CIRCUMFERENCE;
  readonly donutRadius = DONUT_RADIUS;

  constructor(
    private auth: AuthService,
    private exportRequestService: ExportRequestService,
    private adminService: AdminService,
    private projectService: ProjectService,
    private walletService: WalletService
  ) {
    this.session = this.auth.getSession();
  }

  private get canManageProjects(): boolean {
    return perms.canManageProjects(this.session?.role);
  }

  private get canViewProjects(): boolean {
    return perms.canViewProjects(this.session?.role);
  }

  get canReviewCompanies(): boolean {
    return perms.canReviewCompanies(this.session?.role);
  }

  get canManagePlatformUsers(): boolean {
    return perms.canManagePlatformUsers(this.session?.role);
  }

  get hasStatsSources(): boolean {
    return this.canManageProjects || this.canViewProjects || this.canReviewCompanies || this.canManagePlatformUsers;
  }

  ngOnInit(): void {
    if (this.canManageProjects) {
      this.loadPendingExportRequests();
      this.loadWalletBalance();
    }
    if (this.canViewProjects) {
      this.loadProjectCount();
    }
    if (this.canReviewCompanies) {
      this.loadCompanyStatusCounts();
      this.loadCompanyList();
    }
    if (this.canManagePlatformUsers) {
      this.loadPlatformUsers();
      this.loadRoleSummary();
    }
    if (perms.canViewPlatformExportRequests(this.session?.role)) {
      this.loadStatusBreakdown();
      this.loadExportRequestTrend();
    }
    this.loadRecentActivity();
  }

  get canViewTrendChart(): boolean {
    return perms.canViewPlatformExportRequests(this.session?.role);
  }

  get chartAcceptedRate(): number {
    const total = this.chartTotal();
    return total > 0 ? Math.round((this.chartAccepted() / total) * 100) : 0;
  }

  get chartPeriodLabel(): string {
    switch (this.chartPeriod()) {
      case 'week':
        return '7 days';
      case 'month':
        return '4 weeks';
      default:
        return '12 months';
    }
  }

  setChartPeriod(period: ChartPeriod): void {
    if (this.chartPeriod() === period) {
      return;
    }
    this.chartPeriod.set(period);
    this.loadExportRequestTrend();
  }

  // Stats resolve as independent, concurrently-firing HTTP calls, so they can arrive in any
  // order. Re-sorting into this fixed order on every update keeps tile position stable
  // regardless of which request wins the race.
  private readonly statOrder: string[] = [
    'Pending export requests',
    'Your projects',
    'Projects on platform',
    'Wallet balance',
    'Pending approvals',
    'Platform users',
  ];

  private addStat(tile: StatTile): void {
    this.stats.update((current) => {
      const index = current.findIndex((t) => t.label === tile.label);
      const next = index === -1 ? [...current, tile] : [...current.slice(0, index), tile, ...current.slice(index + 1)];
      return next.sort((a, b) => this.statOrder.indexOf(a.label) - this.statOrder.indexOf(b.label));
    });
  }

  private loadPendingExportRequests(): void {
    this.exportRequestService.pendingCount().subscribe({
      next: (res) =>
        this.addStat({
          icon: 'swap_horiz',
          label: 'Pending export requests',
          value: String(res.data.pending),
          link: '/export-requests',
          accent: 'amber',
        }),
      error: () => {},
    });
  }

  private loadProjectCount(): void {
    this.projectService.list(0, 1).subscribe({
      next: (res) =>
        this.addStat({
          icon: 'inventory_2',
          label: this.canManageProjects ? 'Your projects' : 'Projects on platform',
          value: String(res.page?.count ?? res.data.length),
          link: '/projects',
          accent: 'blue',
        }),
      error: () => {},
    });
  }

  private loadWalletBalance(): void {
    this.walletService.getWallet().subscribe({
      next: (res) =>
        this.addStat({
          icon: 'account_balance_wallet',
          label: 'Wallet balance',
          value: res.data.balance.toLocaleString(),
          link: '/wallet',
          accent: 'green',
        }),
      error: () => {},
    });
  }

  // ---- Company review -------------------------------------------------------------

  /**
   * `forkJoin` fails as a unit, so a single failed count would otherwise blank the whole
   * ring. Each count falls back to zero instead, keeping every other slice readable.
   */
  private countOrZero(source: Observable<ApiEnvelope<unknown[]>>): Observable<number> {
    return source.pipe(
      // `page.count` is the full match count, not the length of the page fetched.
      map((res) => res.page?.count ?? res.data.length),
      catchError(() => of(0))
    );
  }

  /**
   * There's no company-stats endpoint, so the per-status counts come from one
   * `size=1` page per status — `page.count` is the full match count, which is accurate
   * regardless of how many companies exist (unlike counting a fetched page client-side).
   */
  private loadCompanyStatusCounts(): void {
    this.isLoadingCompanyCounts.set(true);
    forkJoin(
      COMPANY_STATUS_ORDER.map((entry) => this.countOrZero(this.adminService.listCompanies(entry.status, 0, 1)))
    ).subscribe({
      next: (values) => {
        this.isLoadingCompanyCounts.set(false);
        const counts = values.map((count, i) => ({ ...COMPANY_STATUS_ORDER[i], count }));
        const total = counts.reduce((sum, c) => sum + c.count, 0);
        this.companyTotal.set(total);
        this.companyArcs.set(
          this.layoutDonut(
            counts.map((c) => ({ key: c.status, label: c.label, color: c.color, count: c.count })),
            total
          )
        );

        const pending = counts.find((c) => c.status === 'PENDING')?.count ?? 0;
        this.addStat({
          icon: 'fact_check',
          label: 'Pending approvals',
          value: String(pending),
          link: '/admin/companies',
          accent: 'amber',
        });
      },
      error: () => this.isLoadingCompanyCounts.set(false),
    });
  }

  setCompanyFilter(filter: CompanyFilter): void {
    if (this.companyFilter() === filter) {
      return;
    }
    this.companyFilter.set(filter);
    this.loadCompanyList();
  }

  private loadCompanyList(): void {
    this.isLoadingCompanies.set(true);
    const filter = this.companyFilter();
    this.adminService.listCompanies(filter === 'ALL' ? undefined : filter, 0, 6).subscribe({
      next: (res) => {
        this.isLoadingCompanies.set(false);
        this.companies.set(
          res.data.map((company) => ({
            id: company.id,
            name: company.name,
            countryCode: company.countryCode,
            registrationNo: company.registrationNo,
            status: company.status,
            createdAt: company.createdAt,
          }))
        );
      },
      error: () => this.isLoadingCompanies.set(false),
    });
  }

  get companyEmptyMessage(): string {
    const filter = this.companyFilter();
    return filter === 'ALL'
      ? 'No companies registered yet.'
      : `No ${filter.toLowerCase()} companies right now.`;
  }

  // ---- Platform users -------------------------------------------------------------

  private loadPlatformUsers(): void {
    this.isLoadingUsers.set(true);
    this.adminService.listUsers(undefined, 0, 5).subscribe({
      next: (res) => {
        this.isLoadingUsers.set(false);
        const total = res.page?.count ?? res.data.length;
        this.addStat({
          icon: 'manage_accounts',
          label: 'Platform users',
          value: String(total),
          link: '/admin/users',
          accent: 'violet',
        });
        this.recentUsers.set(
          res.data.map((user) => ({
            id: user.id,
            fullName: user.fullName,
            email: user.email,
            role: user.role,
            enabled: user.enabled,
          }))
        );
      },
      error: () => this.isLoadingUsers.set(false),
    });
  }

  /** Same `size=1`-per-value counting trick as the company statuses — see above. */
  private loadRoleSummary(): void {
    this.isLoadingRoles.set(true);
    forkJoin(
      ROLE_ORDER.map((entry) => this.countOrZero(this.adminService.listUsers(entry.role, 0, 1)))
    ).subscribe({
      next: (values) => {
        this.isLoadingRoles.set(false);
        const rows = values.map((count, i) => ({
          role: ROLE_ORDER[i].role,
          label: ROLE_ORDER[i].label,
          count,
          widthPct: 0,
        }));
        const max = Math.max(1, ...rows.map((r) => r.count));
        this.roleSummary.set(rows.map((row) => ({ ...row, widthPct: (row.count / max) * 100 })));
        this.platformScopedTotal.set(
          rows.filter((r) => PLATFORM_SCOPED.includes(r.role)).reduce((sum, r) => sum + r.count, 0)
        );
        this.companyScopedTotal.set(
          rows.filter((r) => !PLATFORM_SCOPED.includes(r.role)).reduce((sum, r) => sum + r.count, 0)
        );
      },
      error: () => this.isLoadingRoles.set(false),
    });
  }

  firstName(): string {
    return this.session?.fullName?.split(' ')[0] ?? '';
  }

  private loadRecentActivity(): void {
    const role = this.session?.role;

    if (perms.canViewPlatformConfig(role)) {
      this.recentActivityTitle.set('Recent audit events');
      this.recentActivityLink = '/admin/audit-events';
      this.loadRecentAuditEvents();
    } else if (perms.canViewPlatformExportRequests(role)) {
      this.recentActivityTitle.set('Recent export requests');
      this.recentActivityLink = '/admin/export-requests';
      this.loadRecentPlatformExportRequests();
    } else if (this.canManageProjects) {
      this.recentActivityTitle.set('Recent projects');
      this.recentActivityLink = '/projects';
      this.loadRecentProjects();
    }
  }

  private humanizeAuditType(type: AuditEventType): string {
    const words = type.replace(/_/g, ' ').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  private loadRecentAuditEvents(): void {
    this.isLoadingActivity.set(true);
    this.adminService.listAuditEvents({}, 0, 6).subscribe({
      next: (res) => {
        this.isLoadingActivity.set(false);
        this.recentActivity.set(
          res.data.map((event) => ({
            icon: event.outcome === 'FAILURE' ? 'error_outline' : 'check_circle',
            title: this.humanizeAuditType(event.type),
            subtitle: [event.actorLabel, event.companyName].filter(Boolean).join(' · ') || 'System',
            time: event.occurredAt,
            link: '/admin/audit-events',
            badgeStatus: event.outcome,
          }))
        );
      },
      error: () => this.isLoadingActivity.set(false),
    });
  }

  private loadRecentPlatformExportRequests(): void {
    this.isLoadingActivity.set(true);
    this.adminService.listExportRequests({}, 0, 5).subscribe({
      next: (res) => {
        this.isLoadingActivity.set(false);
        this.recentActivity.set(
          res.data.map((request) => ({
            icon: 'swap_horiz',
            title: `${request.exporterCompanyName} → ${request.importerCompanyName}`,
            subtitle: `${request.eventCount} event${request.eventCount === 1 ? '' : 's'}`,
            time: request.createdAt,
            link: `/export-requests/${request.id}`,
            badgeStatus: request.status,
          }))
        );
      },
      error: () => this.isLoadingActivity.set(false),
    });
  }

  private loadStatusBreakdown(): void {
    this.isLoadingBreakdown.set(true);
    this.adminService.exportRequestStats().subscribe({
      next: (res) => {
        this.isLoadingBreakdown.set(false);
        const total = res.data.total;
        this.statusBreakdownTotal.set(total);
        this.statusArcs.set(
          this.layoutDonut(
            STATUS_CHART_ORDER.map((entry) => ({
              key: entry.status,
              label: entry.label,
              color: entry.color,
              count: res.data.byStatus[entry.status] ?? 0,
            })),
            total
          )
        );
      },
      error: () => this.isLoadingBreakdown.set(false),
    });
  }

  /**
   * Turns part-to-whole counts into ring slices. Each slice is a dash run on a full
   * circle: `dash` is "visible-run rest-of-circle" and `offset` walks the run around the
   * ring. Zero-count slices still appear in the legend (so the reader can see a status
   * is at zero rather than missing) but contribute no arc.
   */
  private layoutDonut(
    entries: { key: string; label: string; color: string; count: number }[],
    total: number
  ): DonutArc[] {
    const nonZero = entries.filter((e) => e.count > 0).length;
    // A lone full-circle slice would otherwise show a notch where the gap falls.
    const gap = nonZero > 1 ? DONUT_GAP : 0;
    let cursor = 0;

    return entries.map((entry) => {
      const pct = total > 0 ? (entry.count / total) * 100 : 0;
      const arc = (pct / 100) * DONUT_CIRCUMFERENCE;
      const visible = arc > 0 ? Math.max(arc - gap, 1) : 0;
      const offset = -(cursor + gap / 2);
      cursor += arc;

      return {
        key: entry.key,
        label: entry.label,
        color: entry.color,
        count: entry.count,
        pct,
        dash: `${this.round(visible)} ${this.round(DONUT_CIRCUMFERENCE - visible)}`,
        offset: this.round(offset),
      };
    });
  }

  private round(n: number): number {
    return Math.round(n * 100) / 100;
  }

  // The backend has no time-bucketed stats endpoint, so this buckets client-side from
  // the raw `createdAt` timestamps on GET /admin/export-requests (sorted createdAt DESC,
  // capped at 100/page server-side). It pages backward only as far as the selected
  // period's start, up to MAX_TREND_PAGES, to bound worst-case request count.
  private buildChartBuckets(period: ChartPeriod): ChartBucket[] {
    const now = new Date();
    const buckets: ChartBucket[] = [];

    if (period === 'week') {
      for (let i = 6; i >= 0; i--) {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
        buckets.push({
          label: start.toLocaleDateString(undefined, { weekday: 'short' }),
          start,
          end,
          count: 0,
          accepted: 0,
        });
      }
    } else if (period === 'month') {
      for (let i = 3; i >= 0; i--) {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i * 7 - 6);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
        buckets.push({ label: `Week ${4 - i}`, start, end, count: 0, accepted: 0 });
      }
    } else {
      for (let i = 11; i >= 0; i--) {
        const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        buckets.push({
          label: start.toLocaleDateString(undefined, { month: 'short' }),
          start,
          end,
          count: 0,
          accepted: 0,
        });
      }
    }

    return buckets;
  }

  private loadExportRequestTrend(): void {
    this.isLoadingChart.set(true);
    const buckets = this.buildChartBuckets(this.chartPeriod());
    const cutoff = buckets[0].start;
    this.fetchTrendPage(cutoff, 0, [], buckets);
  }

  private fetchTrendPage(
    cutoff: Date,
    page: number,
    accumulated: AdminExportRequestResponse[],
    buckets: ChartBucket[]
  ): void {
    this.adminService.listExportRequests({}, page, 100).subscribe({
      next: (res) => {
        const combined = accumulated.concat(res.data);
        const oldestInPage = res.data.length > 0 ? new Date(res.data[res.data.length - 1].createdAt) : null;
        const reachedCutoff = oldestInPage !== null && oldestInPage < cutoff;
        const isLastPage = res.data.length < 100;

        if (!reachedCutoff && !isLastPage && page + 1 < MAX_TREND_PAGES) {
          this.fetchTrendPage(cutoff, page + 1, combined, buckets);
        } else {
          this.isLoadingChart.set(false);
          this.applyChartData(combined, buckets);
        }
      },
      error: () => this.isLoadingChart.set(false),
    });
  }

  private applyChartData(records: AdminExportRequestResponse[], buckets: ChartBucket[]): void {
    for (const record of records) {
      const createdAt = new Date(record.createdAt);
      const bucket = buckets.find((b) => createdAt >= b.start && createdAt < b.end);
      if (bucket) {
        bucket.count++;
        if (record.status === 'ACCEPTED') {
          bucket.accepted++;
        }
      }
    }

    this.chartTotal.set(buckets.reduce((sum, b) => sum + b.count, 0));
    this.chartAccepted.set(buckets.reduce((sum, b) => sum + b.accepted, 0));

    const peak = buckets.reduce((best, b) => (b.count > best.count ? b : best), buckets[0]);
    this.chartPeakLabel.set(peak && peak.count > 0 ? peak.label : '—');

    this.chartRender.set(this.layoutChart(buckets));
  }

  /**
   * Geometry is emitted in relative units: bars as a % of plot height, the accepted line in a
   * 0–100 viewBox. Both series are counts of export requests sharing one axis — the line is
   * the accepted subset of the bar it sits under, never a second unrelated measure.
   */
  private layoutChart(buckets: ChartBucket[]): ChartRender {
    const maxCount = Math.max(1, ...buckets.map((b) => b.count));
    const toPct = (value: number) => (value / maxCount) * PLOT_SCALE;

    const bars: BarLayout[] = buckets.map((bucket) => ({
      label: bucket.label,
      count: bucket.count,
      accepted: bucket.accepted,
      acceptedRate: bucket.count > 0 ? Math.round((bucket.accepted / bucket.count) * 100) : 0,
      heightPct: toPct(bucket.count),
      acceptedPct: toPct(bucket.accepted),
    }));

    const points = buckets.map((bucket, i) => ({
      x: ((i + 0.5) / buckets.length) * 100,
      y: 100 - toPct(bucket.accepted),
    }));

    return { bars, acceptedPath: this.buildSmoothPath(points), ticks: this.buildTicks(maxCount) };
  }

  /** Gridline values at clean round intervals, so magnitudes are readable without hovering. */
  private buildTicks(maxCount: number): AxisTick[] {
    const step = this.niceStep(maxCount);
    const ticks: AxisTick[] = [];
    for (let value = 0; value <= maxCount; value += step) {
      ticks.push({ value, pct: (value / maxCount) * PLOT_SCALE });
    }
    return ticks;
  }

  private niceStep(max: number): number {
    if (max <= 4) {
      return 1;
    }
    const raw = max / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const normalized = raw / magnitude;
    const rounded = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return rounded * magnitude;
  }

  /**
   * Catmull-Rom spline emitted as cubic Beziers. Unlike a quadratic-through-midpoints
   * smoothing, this passes exactly through every data point — required here because each
   * point is also marked with a dot, so an approximating curve would visibly miss them.
   */
  private buildSmoothPath(points: { x: number; y: number }[]): string {
    if (points.length === 0) {
      return '';
    }
    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y}`;
    }

    const round = (n: number) => Math.round(n * 100) / 100;
    let d = `M ${round(points[0].x)} ${round(points[0].y)}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] ?? points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] ?? p2;

      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C ${round(c1x)} ${round(c1y)} ${round(c2x)} ${round(c2y)} ${round(p2.x)} ${round(p2.y)}`;
    }

    return d;
  }

  private loadRecentProjects(): void {
    this.isLoadingActivity.set(true);
    this.projectService.list(0, 5).subscribe({
      next: (res) => {
        this.isLoadingActivity.set(false);
        this.recentActivity.set(
          res.data.map((project) => ({
            icon: 'inventory_2',
            title: project.name,
            subtitle: project.code,
            time: project.updatedAt,
            link: `/projects/${project.id}`,
            badgeStatus: project.status,
          }))
        );
      },
      error: () => this.isLoadingActivity.set(false),
    });
  }
}
